import { useEffect, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { Box, Paper, Stack, Typography, useMediaQuery } from "@mui/material";
import { ContextActionMenu } from "../../../contextual-app-bar/ContextActionMenu";
import FullScreenDialog, { FullScreenDialogCloseReason } from "../../../full-screen-dialog/FullScreenDialog";
import { DialogResult, DialogState, BindDialogReturn, useDialogState, bindDialog, DialogProps } from "../../../../hooks/use-dialog-state/UseDialogState";
import SettingsBar, { SettingsActionEnum } from "../../../settings-bar/SettingsBar";
import { AlarmDashboardDetailDialogResultValues, AlarmDashboardDetailDialogInitValues } from "./AlarmDashboardDetailDialogUtils";
import useAlarmDashboardDetailState from "./AlarmDashboardDetailDialogState";
import SideSheet from "../../../side-sheet/side-sheet/SideSheet";
import { AlarmDashboardViewOptions } from "../alarm-dashboard-view-options/AlarmDashboardViewOptions";
import { isMobile, isTablet } from "react-device-detect";
import { NotificationsDetailChart } from "../notifications-detail-chart/NotificationsDetailChart";
import { NotificationsDetailGrid } from "../notifications-detail-grid/NotificationsDetailGrid";

export type AlarmDashboardDetailDialogResultReason = FullScreenDialogCloseReason | "close";

export type AlarmDashboardDetailDialogResult = DialogResult<AlarmDashboardDetailDialogResultValues, AlarmDashboardDetailDialogResultReason>;

export type AlarmDashboardDetailDialogProps = DialogProps<AlarmDashboardDetailDialogInitValues, AlarmDashboardDetailDialogResult>;

export default function AlarmDashboardDetailDialog({
    open,
    onClose,
    values
}: AlarmDashboardDetailDialogProps): JSX.Element {

    const isPhone = isMobile && !isTablet;
    const [openSideSheet, setOpenSideSheet] = useState(false);
    const { t } = useTranslation();
    const matches = useMediaQuery('(max-width:920px)');

    const {
        state,
        setDuration,
        setShowEmail,
        setShowText,
        setShowVoice,
        setSelectedTarget,
        resetState
    } = useAlarmDashboardDetailState();

    const groupID = values?.groupID as number;

    useEffect(() => {
        if (values?.durationOptions) {
            setDuration(values.durationOptions);
        }
    }, [
        values,
        setDuration
    ]);


    useEffect(() => {
        setSelectedTarget(values?.targetObject);
    }, [
        values?.targetObject,
        setSelectedTarget
    ]);

    return (open && values ? <FullScreenDialog open={open} onClose={reason => onClose({ reason: reason })}>

        <SettingsBar title={t('views.alarmDashboardDetail')}
            onClose={
                async (reason: SettingsActionEnum) => {
                    setOpenSideSheet(false);
                    onClose && onClose({ reason: "close" });
                }}
                hideAction={true}
        />
            <Paper sx={{
                    display: "flex",
                    flexDirection: "column",
                    flex: '1 1 auto',
                    overflow: "hidden",
                    //overflowY: 'auto',
                    backgroundColor: "custom.background.cardContainer"
                }}
                elevation={0}
            >

                <Stack
                    direction='row'
                    sx={{
                        display: 'flex',
                        flex: '1 1 auto',
                        overflow: 'hidden'
                    }}> 

                    <Stack
                        direction='column'
                        sx={{
                            display: 'flex',
                            flex: '1 1 auto',
                            overflow: 'hidden'
                        }}> 

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                marginRight: 1
                            }}>

                            <Typography
                                variant='subtitle1'
                                sx={{
                                    margin: 1,
                                    textTransform: 'uppercase',
                                    fontWeight: 600
                                }}>
                                    {t('alarmDashboard.title', { duration: state.durationLabel})}
                            </Typography>

                            <ContextActionMenu 
                                viewOptionState={state.viewOptionState}
                                onOverrideViewOptionsClick={() => {setOpenSideSheet(!openSideSheet)}}
                                buttonColor="default"
                                hideAddToFavoritesAction={true}
                                />
                        </Box>

                    <Stack
                        direction={matches ? 'column' : { sm: 'column', md: 'row' }}
                        spacing={{ xs: 1, sm: 2, md: 3 }}
                        sx={{
                            flex: '1 1 50%',
                            padding: 1,
                            overflow: 'hidden',
                            overflowY: matches ? 'auto' : undefined
                        }}>
                        <Box sx={{
                                    display: 'flex',
                                    minHeight: isPhone ? 500 : undefined,
                                    overflow: 'hidden',
                                    flexBasis: isPhone ? 'auto' : '50%',
                                    flexShrink: isPhone ? '0' : undefined,
                                    minWidth: 300,
                                    maxWidth: { sm: 'auto', md: 500},
                                    
                        }}>
                            <NotificationsDetailChart 
                                notificationsType={values.detailType} 
                                groupID={values.groupID} 
                                durationOptions={state.durationOptions} 
                                showEmail={state.showEmail}
                                showVoice={state.showVoice} 
                                showText={state.showText}
                                targetObject={values?.targetObject} 
                                onTargetSelected={(item) => setSelectedTarget(item)}/>
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            minHeight: isPhone ? 500 : undefined,
                            overflow: 'hidden',
                            flexShrink: isPhone ? '0' : undefined,
                            flexBasis: isPhone ? 'auto%' : matches ? '50%' : '100%'
                        }}>

                            <NotificationsDetailGrid
                                notificationsType={values.detailType} 
                                groupID={values.groupID} 
                                durationOptions={state.durationOptions} 
                                showEmail={state.showEmail}
                                showVoice={state.showVoice} 
                                showText={state.showText}
                                targetObject={state.selectedTarget} />


                        </Box>
                    </Stack>
                </Stack>

                <SideSheet                
                    title={t("options")}
                    open={openSideSheet ?? false}
                    onClose={() => setOpenSideSheet(false)}
                    onReset={resetState}
                >

                    <AlarmDashboardViewOptions groupID={groupID}
                        durationOptions={state.durationOptions} 
                        onDurationChanged={(changedValues) => setDuration(changedValues)}
                        showText={state.showText} 
                        onShowTextChanged={(checked) => setShowText(checked)}
                        showEmail={state.showEmail} 
                        onShowEmailChanged={(checked) => setShowEmail(checked)}
                        showVoice={state.showVoice} 
                        onShowVoiceChanged={(checked) => setShowVoice(checked)} />

                </SideSheet>

            </Stack>

        </Paper>
           
    </FullScreenDialog> : <></>);
}

export type AlarmDashboardDetailDialogState = DialogState<AlarmDashboardDetailDialogInitValues, AlarmDashboardDetailDialogResult>;
export type BindAlarmDashboardDetailDialogReturn = BindDialogReturn<AlarmDashboardDetailDialogInitValues, AlarmDashboardDetailDialogResult>;

export function useAlarmDashboardDetailDialogState(): AlarmDashboardDetailDialogState {
    return useDialogState();
}

export function bindAlarmDashboardDetailDialog(props: AlarmDashboardDetailDialogState): BindAlarmDashboardDetailDialogReturn {
    return bindDialog(props);
}
